import React, { useEffect, useRef } from 'react';
import { Check, X, CornerDownLeft } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  submitted?: boolean;
  disabled?: boolean;
  /** Tras el cierre: llega del servidor. */
  acceptedAnswers?: string[];
  isCorrect?: boolean;
  /** Sin botón de confirmar: en los exámenes la respuesta se guarda sola al cambiarla. */
  hideSubmit?: boolean;
}

export const FillBlankQuestion: React.FC<Props> = ({
  value,
  onChange,
  onSubmit,
  submitted = false,
  disabled = false,
  acceptedAnswers,
  isCorrect,
  hideSubmit = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const revealed = Array.isArray(acceptedAnswers);
  const locked = disabled || submitted || revealed;

  // El teclado debe estar listo en cuanto aparece la pregunta: cada segundo
  // buscando dónde escribir son puntos perdidos.
  useEffect(() => {
    if (!locked) inputRef.current?.focus();
  }, [locked]);

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!locked && !hideSubmit && value.trim()) onSubmit();
        }}
        className="relative"
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={locked}
          maxLength={100}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Escribe tu respuesta…"
          aria-label="Tu respuesta"
          className={`
            w-full px-6 py-6 pr-16 rounded-2xl text-center text-2xl font-semibold
            bg-ink-850 border-2 transition-all outline-none
            placeholder:text-ink-500 placeholder:font-normal placeholder:text-lg
            ${
              revealed
                ? isCorrect
                  ? 'border-success-500 text-success-400 bg-success-500/10'
                  : 'border-danger-500 text-danger-400 bg-danger-500/10'
                : 'border-ink-700 text-ink-50 focus:border-sky-500 focus:bg-ink-800'
            }
            ${submitted && !revealed ? 'opacity-70' : ''}
          `}
        />

        {!locked && !hideSubmit && value.trim() && (
          <button
            type="submit"
            aria-label="Enviar respuesta"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-xl bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
          >
            <CornerDownLeft className="w-5 h-5" strokeWidth={2.5} />
          </button>
        )}

        {revealed && (
          <span className="absolute right-5 top-1/2 -translate-y-1/2">
            {isCorrect ? (
              <Check className="w-7 h-7 text-success-400" />
            ) : (
              <X className="w-7 h-7 text-danger-400" />
            )}
          </span>
        )}
      </form>

      {!revealed && !submitted && (
        <p className="text-center text-ink-500 text-sm">
          No te preocupes por tildes ni mayúsculas
        </p>
      )}

      {submitted && !revealed && (
        <p className="text-center text-ink-400 text-sm">Respuesta enviada. Esperando al resto…</p>
      )}

      {revealed && !isCorrect && (
        <div className="surface p-4 text-center">
          <p className="text-ink-400 text-sm mb-1">
            {acceptedAnswers!.length > 1 ? 'Se aceptaban' : 'La respuesta era'}
          </p>
          <p className="text-success-400 font-semibold text-lg">
            {acceptedAnswers!.join(' · ')}
          </p>
        </div>
      )}
    </div>
  );
};
